/**
 * useAuthSync — subskrybuje onAuthStateChanged i synchronizuje Zustand store.
 * Po zalogowaniu sprawdza czy user ma rodzinę (Firestore query) i zapisuje do store.
 */
import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, IS_DEV_MODE } from '../../lib/firebase';
import { useAuthStore, type Family } from './authStore';
import { findUserFamily, ensurePersonalWallet } from './api';
import { DEV_USER, DEV_FAMILY, activateDevBridge } from '../../lib/devBridge';

const DEV_SESSION_KEY = 'rf-dev-auth';

export function useAuthSync(): void {
  const setUser = useAuthStore((s) => s.setUser);
  const setFamily = useAuthStore((s) => s.setFamily);
  const setAuthReady = useAuthStore((s) => s.setAuthReady);

  useEffect(() => {
    if (IS_DEV_MODE) {
      activateDevBridge();
      // Dev mode: jeśli user był zalogowany przed reload (sessionStorage flag),
      // automatycznie odbuduj stan store z mockami (analog do onAuthStateChanged w prod).
      if (sessionStorage.getItem(DEV_SESSION_KEY) === '1') {
        devSignIn();
      } else {
        setAuthReady(true);
      }
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setFamily(null);
        setAuthReady(true);
        return;
      }

      const user = {
        uid: fbUser.uid,
        email: fbUser.email ?? '',
        name: fbUser.displayName ?? '',
      };
      setUser(user);

      console.info('[useAuthSync] onAuthStateChanged: user signed in', fbUser.uid, '— szukam family...');
      try {
        const found = await findUserFamily(fbUser.uid);
        if (found) {
          console.info('[useAuthSync] family found:', found.id);
          const data = found.data;
          const family: Family = {
            id: found.id,
            name: (data.name as string) ?? '',
            inviteCode: data.inviteCode as string | undefined,
            creatorId: data.creatorId as string | undefined,
            members: (data.members as Record<string, { name: string; email: string }>) ?? {},
          };
          setFamily(family);
          // Naprawa uwięzionych sesji (user był ale portfel zniknął)
          await ensurePersonalWallet(fbUser.uid, found.id, user.name);
        } else {
          console.warn('[useAuthSync] family NOT FOUND — user redirected na /register-family. Czy Firestore był dostępny? (Brave Shields?)');
          setFamily(null);
        }
      } catch (err) {
        const e = err as { code?: string; message?: string };
        console.error('[useAuthSync] family lookup FAILED:', e.code, e.message, err);
        if (e.code === 'permission-denied' || e.message?.includes('Missing or insufficient permissions')) {
          console.error('[useAuthSync] >>> Firestore Rules denied. Sprawdź czy zalogowany user ma poprawny uid + czy rules są up-to-date.');
        }
        if (e.message?.includes('Failed to fetch') || e.message?.includes('network')) {
          console.error('[useAuthSync] >>> NETWORK BLOCKED. Najpewniej AdBlock / Brave Shields blokuje firestore.googleapis.com. Wyłącz Shields dla rodzinkafinance.pl.');
        }
        setFamily(null);
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, [setUser, setFamily, setAuthReady]);
}

// ── Helper dla dev mode logowania (ręcznie ustawia store + sessionStorage flag) ──
export function devSignIn(): void {
  sessionStorage.setItem(DEV_SESSION_KEY, '1');
  useAuthStore.setState({
    user: { uid: DEV_USER.uid, email: DEV_USER.email, name: DEV_USER.name },
    family: {
      id: DEV_FAMILY.id,
      name: DEV_FAMILY.name,
      inviteCode: DEV_FAMILY.inviteCode,
      members: Object.fromEntries(
        Object.entries(DEV_FAMILY.members).map(([k, v]) => [k, { name: v.name, email: v.email }]),
      ),
    },
    authReady: true,
  });
}

export function devSignOut(): void {
  sessionStorage.removeItem(DEV_SESSION_KEY);
  useAuthStore.getState().reset();
  useAuthStore.setState({ authReady: true });
}
